import { colors } from '@globals/themes/defaultTheme'
import { mobileQuery } from '@globals/themes/themeConstants'
import Checkbox from '@mui/material/Checkbox'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { Selectable } from '@shared/filters/selections'
import { RecognitionFormData } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { useT } from '@transifex/react'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

const groupHeaderSx: SxProps = {
  color: colors.grayMedium,
  mt: 0,
  [mobileQuery]: {
    mt: 0,
  },
}

type MobileRecognitionQualitiesViewProps = {
  options?: Selectable[]
}

const MobileRecognitionQualitiesView: React.FC<MobileRecognitionQualitiesViewProps> = ({
  options,
}) => {
  const t = useT()
  const { control, setValue } = useFormContext<RecognitionFormData>()
  const selectedQualities = useWatch({ control, name: 'qualities' }) as Selectable[] | undefined
  const selectedIds = useMemo(
    () => new Set((selectedQualities ?? []).map(q => q.id)),
    [selectedQualities],
  )

  const handleQualityToggle = (quality: Selectable, checked: boolean) => {
    const current = selectedQualities ?? []
    const next = checked ? [...current, quality] : current.filter(q => q.id !== quality.id)
    setValue('qualities', next, { shouldValidate: true })
  }

  const groupedByValue = useMemo(() => {
    const groups = new Map<string, Selectable[]>()
    for (const option of options ?? []) {
      const group = option.tooltipValue ?? ''
      const list = groups.get(group)
      if (list) list.push(option)
      else groups.set(group, [option])
    }
    return Array.from(groups.entries())
  }, [options])

  if (!options || options.length === 0) return null

  return (
    <Stack useFlexGap gap={1.5}>
      {groupedByValue.map(([groupName, qualities]) => (
        <Stack key={groupName} useFlexGap gap={1}>
          <Typography variant="h3" sx={groupHeaderSx}>
            {groupName ? `${t('Value')}: ${groupName}` : ''}
          </Typography>
          {qualities.map(quality => {
            const isSelected = selectedIds.has(quality.id)
            return (
              <Stack
                key={quality.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%">
                <Typography component="span" fontWeight={isSelected ? 600 : 400} variant="body1">
                  {quality.name}
                </Typography>
                <Checkbox
                  checked={selectedIds.has(quality.id)}
                  onChange={(_e, checked) => handleQualityToggle(quality, checked)}
                  id={`checkbox-quality-${quality.id}`}
                />
              </Stack>
            )
          })}
        </Stack>
      ))}
    </Stack>
  )
}

export default MobileRecognitionQualitiesView
